import React, { useEffect, useState } from "react";
import * as Popover from "@radix-ui/react-popover";
import { X } from "lucide-react";

interface TimelineFilterOverlayProps {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    items: { id: string; label: string }[];
    selectedItems: Set<string>;
    onSelectionChange: (selected: Set<string>) => void;
    title: string;
    children: React.ReactNode;
}

const TimelineFilterOverlay: React.FC<TimelineFilterOverlayProps> = (
    {
        isOpen,
        onOpenChange,
        items,
        selectedItems,
        onSelectionChange,
        title,
        children
    }) => {
    const [allSelected, setAllSelected] = useState(selectedItems.size === items.length);

    useEffect(() => {
        setAllSelected(selectedItems.size === items.length);
    }, [selectedItems.size, items.length]);

    const handleToggleAll = () => {
        if (allSelected) {
            onSelectionChange(new Set());
        } else {
            onSelectionChange(new Set(items.map(item => item.id)));
        }
    };

    const handleToggleItem = (id: string) => {
        const newSelection = new Set(selectedItems);
        if (newSelection.has(id)) {
            newSelection.delete(id);
        } else {
            newSelection.add(id);
        }
        onSelectionChange(newSelection);
    };

    return (
        <Popover.Root open={isOpen} onOpenChange={onOpenChange}>
            <Popover.Trigger asChild>
                {children}
            </Popover.Trigger>
            <Popover.Portal>
                <Popover.Content
                    className="bg-white border border-gray-200 rounded shadow-xl z-50"
                    align="start"
                    sideOffset={2}
                >
                    <div
                        className="flex items-center justify-between gap-4 ps-4 pe-2 py-2 border-b border-b-gray-300">
                        <h2 className="text-sm font-semibold">{title}</h2>
                        <Popover.Close
                            className="inline-flex p-1 items-center justify-center text-black/50 hover:text-black/75 focus:outline-none cursor-pointer"
                            aria-label="Fermer"
                        >
                            <X size={12} />
                        </Popover.Close>
                    </div>

                    <div className="max-h-96 overflow-y-auto py-1" style={{ minWidth: '250px' }}>
                        <label className="flex items-center gap-2 px-4 py-1 cursor-pointer hover:bg-gray-50">
                            <input
                                type="checkbox"
                                checked={allSelected}
                                onChange={handleToggleAll}
                                className="h-4 w-4 accent-blue-600 cursor-pointer"
                            />
                            <span className="font-medium text-sm text-gray-900">Tous</span>
                        </label>

                        <div className="border-b border-b-gray-200 my-1"/>

                        {items.map((item) => (
                            <label
                                key={item.id}
                                className="flex items-center gap-2 px-4 py-1 cursor-pointer hover:bg-gray-50"
                            >
                                <input
                                    type="checkbox"
                                    checked={selectedItems.has(item.id)}
                                    onChange={() => handleToggleItem(item.id)}
                                    className="h-4 w-4 accent-blue-600 cursor-pointer"
                                />
                                <span className="text-sm text-gray-700">{item.label}</span>
                            </label>
                        ))}
                    </div>
                </Popover.Content>
            </Popover.Portal>
        </Popover.Root>
    );
};

export default TimelineFilterOverlay;
